<script>
  // src/MapScripting.svelte
  import { onMount, onDestroy, createEventDispatcher } from "svelte";
  import { fade, fly } from "svelte/transition";
  import { addPOIs, removePois, addNumericPOIs, removeNumericPois } from "./mapPois";
  import { addPolygonPOIs, removePolygonPOIs } from "./mapPolygonPois";
  import { addBuildings } from "./mapBuildings";
  import { addVisualLayer } from "./mapVisualLayer";
  // import { createFilterFromFeatures } from "./mapFilter";
  import MapMenu from "./MapMenu.svelte";

  import UA from "ua-parser-js";
  const ua = new UA();
  let isMobile = ua.getResult().device["type"] == "mobile";
  // Exports
  export let tour;
  export let expanded;
  export let map; // Map instance passed as prop

  let tooltipX = 0;
  let tooltipY = 0;
  let tooltipText;
  let layersAdded = false;
  let poisAdded = false;
  let styleLoaded = false; // Track when map style is loaded
  
  function addInitialLayers() {
    if (layersAdded || !map) return;
    layersAdded = true;
    
    if (tour?.mapbox?.visualFeatures?.length > 0) {
      const { visualFeatures, visualFeaturesColor } = tour?.mapbox;
      const layers = map.getStyle().layers;
      const desiredLayer = "waterway";
      const beforeLayerId = layers.find((l) => l.id === desiredLayer)?.id;

      addVisualLayer({
        map,
        beforeLayerId,
        visualFeatures,
        visualFeaturesColor,
      });
    }

    if (tour?.mapbox?.buildings?.length > 0) {
      const {
        mapboxTilesetUrl: tilesetUrl,
        mapboxTilesetLayerName: tilesetLayerName,
        labelsUrl,
        labelsLayerName,
        buildingColor,
        buildingHoverColor,
        buildings,
      } = tour?.mapbox;

      addBuildings({
        map,
        tilesetUrl,
        tilesetLayerName,
        labelsUrl,
        labelsLayerName,
        buildingColor,
        buildings,
        buildingHeight: 7,
        buildingHoverColor,
        setTooltip,
        scenes: tour?.scenes,
      });
    }
  }
  
  function addPOIsToMap() {
    if (!map || !expanded || !styleLoaded || poisAdded) return;
    
    poisAdded = true;
    
    if (tour?.mapbox?.pois?.length > 0) {
      const { pois, poiBgColor, poiTextColor } = tour?.mapbox;
      const bgColor = poiBgColor || tour?.theme?.primaryColor;
      const textColor = poiTextColor || "#fff";

      // Handle async addPOIs call
      (async () => {
        try {
          await addPOIs({
            map,
            setTooltip,
            pois,
            bgColor,
            textColor,
            scenes: tour?.scenes,
            slug: tour?.slug,
          });
        } catch (error) {
          console.error('Failed to add POIs:', error);
        }
      })();
    }

    if (tour?.mapbox?.numericPois?.length > 0) {
      const { numericPois, numericPoiBgColor, numericPoiTextColor } = tour?.mapbox;
      const bgColor = numericPoiBgColor || tour?.theme?.primaryColor;
      const textColor = numericPoiTextColor || "#fff";
      addNumericPOIs({
        map,
        setTooltip,
        pois: numericPois,
        bgColor,
        textColor,
        scenes: tour?.scenes,
      });
    }

    if (tour?.mapbox?.polygonPois?.length > 0) {
      const { polygonPois, polygonPoiBgColor, polygonPoiTextColor } = tour?.mapbox;
      const bgColor = polygonPoiBgColor || tour?.theme?.primaryColor;
      const textColor = polygonPoiTextColor || "#fff";
      
      // Handle async addPolygonPOIs call
      (async () => {
        try {
          await addPolygonPOIs({
            map,
            setTooltip,
            polygonPois,
            bgColor,
            textColor,
            scenes: tour?.scenes,
            slug: tour?.slug,
          });
        } catch (error) {
          console.error('Failed to add polygon POIs:', error);
        }
      })();
    }
  }

  const dispatch = createEventDispatcher();

  // function drawFilterFeatures(features, color = "#ff0000") {
  //   const sourceName = `filter-${Math.random().toString(36).substring(7)}`;
  //   map.addSource(sourceName, {
  //     type: "geojson",
  //     data: {
  //       type: "FeatureCollection",
  //       features,
  //     },
  //   });
  //   map.addLayer({
  //     id: sourceName,
  //     type: "line",
  //     source: sourceName,
  //     paint: {
  //       "line-color": color,
  //       "line-width": 2,
  //     },
  //   });
  // }
  // const hasInclusionFilter = tour?.mapbox?.inclusionFilterFeatures?.length > 0;
  // const hasExclusionFilter = tour?.mapbox?.exclusionFilterFeatures?.length > 0;
  // const excludeLayers = [
  //   "clusters",
  //   "cluster-count",
  //   "unclustered-point",
  //   "tour-buildings-layer",
  //   "tour-bg-buildings-layer",
  //   "tour-buildings-layer-labels",
  //   "tour-bg-buildings-layer-labels",
  //   "visual-layer-labels",
  //   "visual-layer",
  // ];

  // function applyFilters() {
  //   let layers = map.getStyle().layers;
  //   if (hasInclusionFilter || hasExclusionFilter) {
  //     layers = layers.filter((layer) => !excludeLayers.includes(layer.id));
  //     layers.forEach((layer) => {
  //       if (hasInclusionFilter) {
  //         map.setFilter(
  //           layer.id,
  //           createFilterFromFeatures(tour?.mapbox?.inclusionFilterFeatures),
  //         );
  //       }
  //       if (hasExclusionFilter) {
  //         map.setFilter(
  //           layer.id,
  //           createFilterFromFeatures(
  //             tour?.mapbox?.exclusionFilterFeatures,
  //             false,
  //           ),
  //         );
  //       }
  //     });
  //   }
  // }

  // function highlightExcludedFeatures() {
  //   if (hasExclusionFilter) {
  //     const exclusionFilter = createFilterFromFeatures(
  //       tour?.mapbox?.exclusionFilterFeatures,
  //       false,
  //     );
  //     map.addLayer({
  //       id: "excluded-features",
  //       type: "fill",
  //       source: "composite",
  //       filter: exclusionFilter,
  //       paint: {
  //         "fill-color": "#ff0000",
  //         "fill-opacity": 0.5,
  //       },
  //     });
  //   }
  // }

  function setTooltip(tooltip) {
    if (!tooltip) {
      tooltipX = 0;
      tooltipY = 0;
      tooltipText = false;
      return;
    }

    const { x, y, text } = tooltip;
    tooltipX = x;
    tooltipY = y;
    tooltipText = text;
  }

  onMount(async () => {
    if (!map) {
      console.warn('Map prop not available in MapScripting');
      return;
    }
    
    // Set up event listeners
    map.on("zoom", () => {
      console.log("zoom", map.getZoom());
    });

    map.on("click", function (e) {
      console.log("event:", e);
      const center = map.getCenter();
      const json = {
        mapCenter: [center.lng, center.lat],
        mapBearing: map.getBearing(),
        mapPitch: map.getPitch(),
        zoomExpandedMap: map.getZoom(),
      };
      console.log(json);
    });
    
    // Add layers when map style is loaded
    if (!layersAdded) {
      if (map.isStyleLoaded()) {
        addInitialLayers();
        styleLoaded = true;
      } else {
        map.once('style.load', () => {
          addInitialLayers();
          styleLoaded = true;
        });
      }
    } else {
      // Layers already added, check if style is loaded
      if (map.isStyleLoaded()) {
        styleLoaded = true;
      }
    }
  });
  
  // Watch for map style loading (reactive statement)
  $: if (map && !styleLoaded) {
    if (map.isStyleLoaded()) {
      styleLoaded = true;
    } else {
      map.once('style.load', () => {
        styleLoaded = true;
      });
    }
  }
  
  // Reactive statement: when expanded changes or style loads, add or remove POIs
  $: if (expanded && map && styleLoaded) {
    // Add POIs when map is expanded and style is loaded
    if (!poisAdded) {
      addPOIsToMap();
    }
  } else if (map && !expanded) {
    // When collapsing, remove POIs and reset flag
    poisAdded = false;
    removePois({ map });
    removeNumericPois({ map });
    removePolygonPOIs({ map });
  }

  onDestroy(() => {
    // Clean up POIs and event listeners
    if (map) {
      removePois({ map });
      removeNumericPois({ map });
      removePolygonPOIs({ map });
    }
  });
</script>

{#if expanded && !isMobile}
  <MapMenu {tour} {map} />
{/if}

{#if !isMobile && tooltipText}
  <div
    class="tooltip"
    style={`top: ${tooltipY - 50}px; left: ${tooltipX - 50}px;`}
  >
    {tooltipText}
  </div>
{/if}

<style>
  :root {
    --tooltip-background: #000;
    --tooltip-color: #fff;
    --tooltip-padding: 0.5rem;
    --tooltip-border-radius: 15px;
  }

  .tooltip {
    position: absolute;
    background: var(--tooltip-background);
    color: var(--tooltip-color);
    padding: var(--tooltip-padding);
    z-index: 999;
    border-radius: var(--tooltip-border-radius);
  }
</style>
